
import { Component, Vue, Prop } from 'vue-property-decorator';

import { sharedState } from '../../../../framework/state';
import { NotificationRecipient } from '../../../../../common/application/model/NotificationRecipient';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../framework/client/resource';
import { errorToast, successToast } from '../../../service/toast_service';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import ParameterService from '../../../service/parameter_service';
import { getDatabaseOptions, getOptionLabel, OptionValue } from '../../../../framework/service/options';
import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';
import { Account } from '../../../../../common/application/model/Account';
import { getCustomerOptions } from '../../../service/application_options_service';
import { getResourceLabels } from '../../../../framework/service/label_service';

@Component({
    components: { AutoCompleteMultipleField },
})
export default class NotificationRecipients extends Vue {
    readonly resourceType = ApplicationResource.NOTIFICATION_RECIPIENT;
    readonly parameterService = new ParameterService(this.resourceType);

    @Prop(String) readonly customerId!: string;

    shared = sharedState;

    local = {
        rows: [] as NotificationRecipient[],
        total: 0,
        loading: false,
        selected: undefined as NotificationRecipient | undefined,
        parameters: this.parameterService.get(),
        customerOptions: [] as OptionValue[],
        notificationTypeOptions: [] as OptionValue[],
        vehicleContractLabels: new Map<string, string>(),
    };

    async mounted() {
        await this.loadAsyncData();
    }

    async dropdownClosed(resourceIds: string[], idField: string): Promise<void> {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    onRowClick(row: Account): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    add() {
        this.$router.push(`/${this.resourceType}/add`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    getCustomerName(id: string): string {
        return getOptionLabel(id, this.local.customerOptions);
    }

    getVehicleContractLabel(id: string) {
        if (this.local.vehicleContractLabels.has(id)) {
            return this.local.vehicleContractLabels.get(id);
        } else {
            return id;
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get customerIds(): string[] {
        return this.local.parameters.customerId || [];
    }

    getNotificationTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.notificationTypeOptions);
    }

    private async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;
        this.local.rows = [];
        const rows = await getResourcesNew<NotificationRecipient>(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        );
        rows.forEach((row) => {
            this.local.rows.push(row);
        });
        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.customerOptions = await getCustomerOptions();
        this.local.notificationTypeOptions = await getDatabaseOptions('NotificationType', this);
        await this.getVehicleContractLabels();

        this.local.loading = false;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    private async getVehicleContractLabels(): Promise<void> {
        const results = await getResourceLabels(
            this.local.rows,
            'vehicleContractId',
            ApplicationResource.VEHICLE_CONTRACT,
            'vehicleRegistrationNumber',
        );
        this.local.vehicleContractLabels = new Map([...this.local.vehicleContractLabels, ...results]);
    }
}
